'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Participant {
  id: number;
  name: string;
  image: string;
  alt: string;
  role: 'instructor' | 'student';
  isOnline: boolean;
  isMuted: boolean;
  isVideoOn: boolean;
}

interface ParticipantsListProps {
  participants: Participant[];
}

const ParticipantsList = ({ participants }: ParticipantsListProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="h-full bg-card rounded-lg border border-border p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-muted rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted rounded w-3/4"></div>
                  <div className="h-2 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filteredParticipants = participants.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const instructors = filteredParticipants.filter(p => p.role === 'instructor');
  const students = filteredParticipants.filter(p => p.role === 'student');

  return (
    <div className="h-full flex flex-col bg-card rounded-lg border border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="UsersIcon" size={20} variant="outline" className="text-primary" />
            <h3 className="font-headline font-semibold text-foreground">
              Participants ({participants.length})
            </h3>
          </div>
        </div>
        <div className="relative">
          <Icon 
            name="MagnifyingGlassIcon" 
            size={18} 
            variant="outline" 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search participants..."
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm text-foreground bg-background"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {instructors.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Instructors
            </h4>
            <div className="space-y-2">
              {instructors.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <AppImage
                        src={participant.image}
                        alt={participant.alt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      {participant.isOnline && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-card rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{participant.name}</p>
                      <p className="text-xs text-primary">Instructor</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon
                      name={participant.isMuted ? 'MicrophoneIcon' : 'MicrophoneIcon'}
                      size={16}
                      variant="solid"
                      className={participant.isMuted ? 'text-error' : 'text-success'}
                    />
                    <Icon
                      name={participant.isVideoOn ? 'VideoCameraIcon' : 'VideoCameraSlashIcon'}
                      size={16}
                      variant="solid"
                      className={participant.isVideoOn ? 'text-success' : 'text-error'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {students.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Students ({students.length})
            </h4>
            <div className="space-y-2">
              {students.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <AppImage
                        src={participant.image}
                        alt={participant.alt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      {participant.isOnline && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-card rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{participant.name}</p>
                      <p className="text-xs text-muted-foreground">Student</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon
                      name={participant.isMuted ? 'MicrophoneIcon' : 'MicrophoneIcon'}
                      size={16}
                      variant="solid"
                      className={participant.isMuted ? 'text-error' : 'text-success'}
                    />
                    <Icon
                      name={participant.isVideoOn ? 'VideoCameraIcon' : 'VideoCameraSlashIcon'}
                      size={16}
                      variant="solid"
                      className={participant.isVideoOn ? 'text-success' : 'text-error'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredParticipants.length === 0 && (
          <div className="text-center py-8">
            <Icon name="UserGroupIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No participants found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantsList;